import type { Clerk } from "@clerk/clerk-js";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { Suspense } from "react";
import {
	ConfigProvider,
	FullscreenLoading,
	getConfig,
	ThirdPartyProviders,
	Toaster,
	TooltipProvider,
} from "@/components";
import {
	getOrCreateCloudContext,
	getOrCreateCloudNamespaceContext,
	getOrCreateEngineContext,
	getOrCreateEngineNamespaceContext,
	getOrCreateOrganizationContext,
	getOrCreateProjectContext,
} from "./app/data-providers/cache";
import { NotFoundCard } from "./app/not-found-card";
import { RouteLayout } from "./app/route-layout";
import { queryClient } from "./queries/global";
import { routeTree } from "./routeTree.gen";

declare module "@tanstack/react-query" {
	interface Register {
		queryMeta: {
			mightRequireAuth?: boolean;
			statusCheck?: boolean;
			reportType?: string;
		};
	}
}

export function createAppRouter(clerk: Clerk) {
	return createRouter({
		basepath: import.meta.env.BASE_URL,
		routeTree,
		context: {
			clerk:
				__APP_TYPE__ === "cloud" ? clerk : (undefined as unknown as Clerk),
			queryClient: queryClient,
			getOrCreateCloudContext,
			getOrCreateEngineContext,
			getOrCreateOrganizationContext,
			getOrCreateProjectContext,
			getOrCreateCloudNamespaceContext,
			getOrCreateEngineNamespaceContext,
		},
		defaultPreloadStaleTime: 0,
		defaultGcTime: 0,
		defaultPreloadGcTime: 0,
		defaultStaleTime: Infinity,
		scrollRestoration: true,
		defaultPendingMinMs: 300,
		defaultPendingComponent: FullscreenLoading,
		defaultOnCatch: (error) => {
			console.error("Router caught an error:", error);
			Sentry.captureException(error);
		},
		defaultNotFoundComponent: () => (
			<RouteLayout>
				<NotFoundCard />
			</RouteLayout>
		),
	});
}

declare module "@tanstack/react-router" {
	interface Register {
		router: ReturnType<typeof createAppRouter>;
	}
}

function InnerApp({ router }: { router: ReturnType<typeof createAppRouter> }) {
	return <RouterProvider router={router} />;
}

export function App({ router }: { router: ReturnType<typeof createAppRouter> }) {
	return (
		<QueryClientProvider client={queryClient}>
			<ConfigProvider value={getConfig()}>
				<ThirdPartyProviders>
					<Suspense fallback={<FullscreenLoading />}>
						<TooltipProvider>
							<InnerApp router={router} />
						</TooltipProvider>
					</Suspense>
				</ThirdPartyProviders>

				<Toaster />
			</ConfigProvider>

			<ReactQueryDevtools client={queryClient} />
		</QueryClientProvider>
	);
}
